import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { BarChart3, CreditCard, ListPlus, Repeat, ShieldCheck, Trophy } from "lucide-react";
import { BID_STEP, MIN_BID, formatIDR } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { ScrollProgress } from "@/components/motion-kit";

export const Route = createFileRoute("/cara-kerja")({
  head: () => ({
    meta: [
      { title: "Cara Kerja Papan Promosi — jasasebar.com" },
      {
        name: "description",
        content:
          "Daftar gratis, naik peringkat dengan bayar via pembayaran online, pantau momentum dan klik. Begini cara papan jasasebar.com bekerja.",
      },
      { property: "og:title", content: "Cara Kerja Papan Promosi — jasasebar.com" },
      { property: "og:description", content: "Daftar gratis, naik peringkat via pembayaran online, pantau momentum & klik." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "/cara-kerja" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/cara-kerja" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Cara naik peringkat di jasasebar.com",
          step: [
            { "@type": "HowToStep", name: "Daftar gratis", text: "Masukkan nama, tautan, dan kategori listing." },
            { "@type": "HowToStep", name: "Bayar untuk naik", text: "Bayar di atas total pesaing." },
            { "@type": "HowToStep", name: "Peringkat berubah", text: "Posisi diperbarui otomatis setelah pembayaran dikonfirmasi." },
          ],
        }),
      },
    ],
  }),
  component: HowPage,
});

const steps = [
  { icon: ListPlus, title: "Daftar gratis", body: "Masukkan nama, tautan, dan kategori. Listing langsung tampil di papan." },
  {
    icon: CreditCard,
    title: "Bayar untuk naik",
    body: `Mulai ${formatIDR(MIN_BID)}. Untuk menyalip sebuah posisi, bayar ${formatIDR(BID_STEP)} di atas totalnya.`,
  },
  { icon: Trophy, title: "Peringkat berubah", body: "Setelah pembayaran dikonfirmasi, posisi diperbarui otomatis." },
  { icon: Repeat, title: "Auto-bid menjaga posisi", body: "Pasang anggaran maksimum; sistem menyalip otomatis saat Anda tersalip." },
  { icon: BarChart3, title: "Pantau momentum", body: "Grafik riwayat bid dan klik harian tersedia di tiap halaman detail." },
  { icon: ShieldCheck, title: "Poin & badge", body: "Klik dan bid mengumpulkan poin — naikkan level dan raih badge Populer." },
];

function HowPage() {
  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />
      <main className="hero-canvas">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl"
          >
            <h1 className="font-display text-4xl font-semibold">Cara kerja</h1>
            <p className="mt-3 text-muted-foreground">
              Papan yang jujur: urutan ditentukan total kontribusi, bukan algoritma tersembunyi.
            </p>
          </motion.div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="surface-card p-6"
              >
                <span className="grid size-10 place-items-center rounded-xl bg-primary-soft text-accent-foreground">
                  <step.icon className="size-5" />
                </span>
                <h2 className="mt-4 font-display text-lg font-semibold">{step.title}</h2>
                <p className="mt-1.5 text-sm text-muted-foreground">{step.body}</p>
              </motion.div>
            ))}
          </div>

          <div className="surface-card mt-8 flex flex-wrap items-center justify-between gap-4 p-6">
            <p className="text-sm text-muted-foreground">Siap tampil di papan? Daftarkan jasa Anda sekarang.</p>
            <Link
              to="/"
              hash="daftar"
              className="inline-flex h-11 items-center rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground"
            >
              Daftar gratis
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
